import {SsaiAdBreakMetadata, SsaiAdMetadata, SsaiAdQuartile, SsaiAdQuartileMetadata} from '../../api/AdapterAPI';
import {AD_TYPE} from '../../enums/AdType';
import {AdInfoVastData} from '../../types/ads/AdInfoVastData';
import {AdSample} from '../../types/AdSample';
import {AnalyticsError} from '../../types/AnalyticsConfig';
import {customDataValuesKeys} from '../../types/CustomDataValues';
import type {Sample} from '../../types/Sample';
import {isNotNullish, isNullish} from '../../utils/Utils';
import {generateUUID} from '../../utils/uuidUtils';

import {
  buildAdSample,
  getCurrentMonotonicTimestampInMs,
  getMonotonicTimestampInMsSince,
  SSAI_RELATED_SAMPLE_MARKER,
} from './ssaiUtils';

export type SsaiEventHandler = {
  getCurrentSample: () => Readonly<Sample>;
  getPlayerCurrentTime: () => number;
  reportPlaybackInteraction: (timestamp: number, playerCurrentTime: number) => void;
  sendAdSample: (adSample: AdSample, options?: {isClosing?: boolean}) => void;
  sendErrorAdSample: (adSample: AdSample, originalError?: any) => void;
};

type AdMetadata = Pick<
  AdInfoVastData,
  'adTitle' | 'advertiserName' | 'creativeAdId' | 'creativeId' | 'universalAdIdRegistry' | 'universalAdIdValue'
> &
  SsaiAdMetadata;

export type InternalSsaiMetadata = AdMetadata & SsaiAdBreakMetadata;

enum SsaiState {
  AD_BREAK_STARTED,
  ACTIVE,
  IDLE,
}

// we start with -1, to be able to increment it before sending the first sample
const INITIAL_INDEX_VALUE = -1;

export class SsaiService {
  /**
   * if `undefined` then the service is not active and should not do anything,
   * why: Because not every adapter supports SSAI
   */
  private eventHandler: SsaiEventHandler | undefined = undefined;

  /** Index of the current ad in the whole session. This index will reset on a source change (new session) */
  private adIndex: number = INITIAL_INDEX_VALUE;

  /**
   * Index of the active ad in the current adBreak. This index will reset on each `adBreakEnd` event.
   */
  private adPodPosition: number = INITIAL_INDEX_VALUE;

  private state: SsaiState = SsaiState.IDLE;
  private isFirstSampleOfAd = false;

  /** Counts the completed Slates during an adBreak. */
  private completedSlates: number | undefined;

  /** Counts the completed Paid Ads during an adBreak. */
  private completedPaidAds: number | undefined;

  /**
   * represents the current playing ad metadata
   * is `undefined` when no ad is playing (after endAdBreak)
   * or omitted by customer
   */
  private currentAdMetadata: InternalSsaiMetadata | undefined;

  /**
   * The impression id for the currently playing ad
   * is undefined when no ad is playing
   */
  private currentAdImpressionId: string | undefined;

  /**
   * The impression id created by an error that happened before the first ad of the ad break started.
   * A first ad that still starts reuses it, so that the error and the ad share one ad impression.
   */
  private pendingFirstAdImpressionId: string | undefined;

  /** List of quartiles already received for the current ad */
  private receivedQuartiles: SsaiAdQuartile[] = [];

  /**
   * Flags whether an error was sent during an ad to avoid sending multiple errors for one ad,
   * which would skew e.g. the ad error percentage.
   */
  private errorSent: boolean = false;

  /**
   * Flags whether an abandonment sample was sent during an ad to avoid sending multiple abandonment samples within one
   * adBreak which would skew e.g. the ad abandonment rate.
   */
  private abandonmentSent: boolean = false;

  /**
   * The timestamp marking when the `adStart` method was called.
   * This timestamp is used to calculate the time difference between the `adStart` call
   * and subsequent {@link adQuartileFinished} or {@link onError} calls.
   */
  private timestampOfAdStart: number | undefined;

  activate(eventHandler: SsaiEventHandler) {
    this.eventHandler = eventHandler;
  }

  adBreakStart(adBreakMetadata?: SsaiAdBreakMetadata) {
    if (!isActivated(this.eventHandler)) {
      // nothing to do if service is not active
      return;
    }

    if (this.state != SsaiState.IDLE) {
      return;
    }

    this.state = SsaiState.AD_BREAK_STARTED;
    this.currentAdMetadata = {
      ...adBreakMetadata,
    };

    if (isNotNullish(this.currentAdMetadata.expectedSlates)) {
      this.completedSlates = 0;
    }

    if (isNotNullish(this.currentAdMetadata.expectedPaidAds)) {
      this.completedPaidAds = 0;
    }
  }

  adStart(adMetadata?: AdMetadata) {
    if (!isActivated(this.eventHandler)) {
      // nothing to do if service is not active
      return;
    }

    if (this.state == SsaiState.IDLE) {
      return;
    }

    const timestamp = new Date().getTime();
    const playerCurrentTime = this.eventHandler.getPlayerCurrentTime();

    // report playback sample - will be saved in our playback related data lake
    // - with the first adStart call we need to close the current normal playback sample (no ssai ad information yet because SsaiState is not yet set to active)
    // - with all following adStart calls, we will deliver previous ssai ad information (SsaiState is already set to active)
    this.eventHandler.reportPlaybackInteraction(timestamp, playerCurrentTime);

    this.state = SsaiState.ACTIVE;
    // to include the adIndex in the next sample which will be the first sample of this started ad
    this.isFirstSampleOfAd = true;

    // an error before the first ad of this ad break already claimed index, pod position and impression id for this ad
    const isFirstAdClaimedByError = isNotNullish(this.pendingFirstAdImpressionId);
    if (!isFirstAdClaimedByError) {
      this.adIndex++;
      this.adPodPosition++;
    }

    this.resetAdData();

    this.currentAdMetadata = {
      adPosition: this.currentAdMetadata?.adPosition,
      adId: adMetadata?.adId,
      adSystem: adMetadata?.adSystem,
      customData: adMetadata?.customData,
      durationInMs: adMetadata?.durationInMs,
      isSlate: adMetadata?.isSlate,
      expectedPaidAds: this.currentAdMetadata?.expectedPaidAds,
      expectedSlates: this.currentAdMetadata?.expectedSlates,
      universalAdIdValue: adMetadata?.universalAdIdValue,
      universalAdIdRegistry: adMetadata?.universalAdIdRegistry,
      creativeId: adMetadata?.creativeId,
      creativeAdId: adMetadata?.creativeAdId,
      advertiserName: adMetadata?.advertiserName,
      adTitle: adMetadata?.adTitle,
    };

    this.currentAdImpressionId = this.pendingFirstAdImpressionId ?? generateUUID();
    this.pendingFirstAdImpressionId = undefined;
    this.timestampOfAdStart = getCurrentMonotonicTimestampInMs();

    // create and send engagement sample - will be saved in our ad related data lake
    this.eventHandler.sendAdSample({
      ...buildAdSample({
        playbackSample: this.eventHandler.getCurrentSample(),
        adIndex: this.adIndex,
        currentAdImpressionId: this.currentAdImpressionId,
        currentAdMetadata: this.currentAdMetadata ?? {},
        quartile: 'started',
        quartileMetadata: undefined,
        errorCode: undefined,
        errorMessage: undefined,
        errorSeverity: undefined,
        timeSinceAdStartedInMs: undefined,
        adPodPosition: this.adPodPosition,
        completedSlates: this.completedSlates,
        completedPaidAds: this.completedPaidAds,
        exitedAdBreak: false,
        closed: undefined,
      }),
    });
  }

  adQuartileFinished(adQuartile: SsaiAdQuartile, adQuartileMetadata: SsaiAdQuartileMetadata) {
    if (!isActivated(this.eventHandler)) {
      // nothing to do if service is not activated
      return;
    }

    if (this.state != SsaiState.ACTIVE) {
      return;
    }

    if (this.receivedQuartiles.includes(adQuartile)) {
      // early return to skip and to not send the duplicate quartile events (deduplication guard)
      return;
    }
    this.receivedQuartiles.push(adQuartile);

    if (adQuartile === 'completed') {
      if (this.currentAdMetadata?.isSlate && isNotNullish(this.completedSlates)) {
        this.completedSlates++;
      } else if (!this.currentAdMetadata?.isSlate && isNotNullish(this.completedPaidAds)) {
        this.completedPaidAds++;
      }
    }

    // create and send engagement sample - will be saved in our ad related data lake
    this.eventHandler.sendAdSample({
      ...buildAdSample({
        playbackSample: this.eventHandler.getCurrentSample(),
        adIndex: this.adIndex,
        currentAdImpressionId: this.currentAdImpressionId!,
        currentAdMetadata: this.currentAdMetadata ?? {},
        quartile: adQuartile,
        quartileMetadata: adQuartileMetadata,
        timeSinceAdStartedInMs: getMonotonicTimestampInMsSince(this.timestampOfAdStart),
        errorCode: undefined,
        errorMessage: undefined,
        errorSeverity: undefined,
        adPodPosition: this.adPodPosition,
        completedSlates: this.completedSlates,
        completedPaidAds: this.completedPaidAds,
        exitedAdBreak: false,
        closed: undefined,
      }),
    });
  }

  adBreakEnd() {
    if (!isActivated(this.eventHandler)) {
      // nothing to do if service is not active
      return;
    }

    if (this.state == SsaiState.IDLE) {
      return;
    }

    if (this.state == SsaiState.ACTIVE) {
      // send an additional terminal sample of this ad break with exitedAdBreak set to true
      this.eventHandler.sendAdSample({
        ...buildAdSample({
          playbackSample: this.eventHandler.getCurrentSample(),
          adIndex: this.adIndex,
          currentAdImpressionId: this.currentAdImpressionId!,
          currentAdMetadata: this.currentAdMetadata ?? {},
          quartile: undefined,
          quartileMetadata: undefined,
          errorCode: undefined,
          errorMessage: undefined,
          errorSeverity: undefined,
          timeSinceAdStartedInMs: getMonotonicTimestampInMsSince(this.timestampOfAdStart),
          adPodPosition: this.adPodPosition,
          completedSlates: this.completedSlates,
          completedPaidAds: this.completedPaidAds,
          exitedAdBreak: true,
          closed: undefined,
        }),
      });

      const timestamp = new Date().getTime();
      const playerCurrentTime = this.eventHandler.getPlayerCurrentTime();
      this.eventHandler.reportPlaybackInteraction(timestamp, playerCurrentTime);
    }

    this.resetAdBreakData();
  }

  manipulate(sample: Sample): void {
    if (!isActivated(this.eventHandler)) {
      // nothing to do if service is not active
      return;
    }

    if (this.state != SsaiState.ACTIVE) {
      return;
    }

    sample.ad = AD_TYPE.SSAI;
    sample.adId = this.currentAdMetadata?.adId;
    sample.adSystem = this.currentAdMetadata?.adSystem;
    sample.adPosition = this.currentAdMetadata?.adPosition;
    // we only mark the first sample of an ssai ad with the index in the playback/audience data (non ad table)
    // this allows us to count the number of ads
    if (this.isFirstSampleOfAd) {
      sample.adIndex = this.adIndex;
      this.isFirstSampleOfAd = false;
    }

    const customData = this.currentAdMetadata?.customData;
    if (isNotNullish(customData)) {
      customDataValuesKeys.forEach((key) => {
        if (customData[key]) {
          sample[key] = customData[key];
        }
      });
    }

    sample[SSAI_RELATED_SAMPLE_MARKER] = true; // mark it as ssai sample
  }

  onError(error: AnalyticsError, originalError: any) {
    if (!isActivated(this.eventHandler)) {
      // nothing to do if service is not active
      return;
    }

    if (this.state === SsaiState.IDLE) {
      return;
    }

    // only send one error sample per ad, and one for an ad break that fails before its first ad
    if (this.errorSent) {
      return;
    }

    // An error before the first `adStart` is attributed to the ad that the ad break was about to play, so it claims
    // the index, pod position and impression id that first ad would have received. A first ad that still starts
    // reuses them, and if the ad break ends without any ad they stay consumed so the next ad break cannot reuse them.
    const isAdBreakError = this.state === SsaiState.AD_BREAK_STARTED;
    if (isAdBreakError) {
      this.adIndex++;
      this.adPodPosition++;
      this.pendingFirstAdImpressionId = generateUUID();
    }

    // both states carry an impression id at this point, so this only guards against sending an ad sample without one
    const adImpressionId = isAdBreakError ? this.pendingFirstAdImpressionId : this.currentAdImpressionId;
    if (isNullish(adImpressionId)) {
      return;
    }

    // we do not assume an error is terminal for the ad break, so the error sample is not flagged with exitedAdBreak
    this.eventHandler.sendErrorAdSample(
      {
        ...buildAdSample({
          playbackSample: this.eventHandler.getCurrentSample(),
          adIndex: this.adIndex,
          currentAdImpressionId: adImpressionId,
          currentAdMetadata: this.currentAdMetadata ?? {},
          quartile: undefined,
          quartileMetadata: undefined,
          errorCode: error.code,
          errorMessage: error.message,
          errorSeverity: error.severity,
          timeSinceAdStartedInMs: getMonotonicTimestampInMsSince(this.timestampOfAdStart),
          adPodPosition: this.adPodPosition,
          completedSlates: this.completedSlates,
          completedPaidAds: this.completedPaidAds,
          exitedAdBreak: false,
          closed: undefined,
        }),
      },
      originalError,
    );

    this.errorSent = true;
  }

  /**
   * Should be used when SSAI ad break was abandoned (`closed: 1` in AdSample) by some unexpected termination (e.g., due
   * to player/browser unload, source change, start/end of casting) to not lose but send the last ad sample of ad break.
   *
   * This indicates that the ad break has ended (`exitedAdBreak: true`), this is important for the ad break
   * completion rate metric.
   *
   * This should not call {@link SsaiEventHandler#reportPlaybackInteraction}, compared to other service methods,
   * because it is not triggered by a user interaction (or SSAI standard flow on THEOplayer) and responsibility to
   * send the last playback sample is on the adapter.
   */
  onAbandonment(options?: {isClosing?: boolean}) {
    if (!isActivated(this.eventHandler)) {
      // nothing to do if service is not active
      return;
    }

    if (this.state != SsaiState.ACTIVE) {
      return;
    }

    // only send one abandonment sample per adBreak
    if (this.abandonmentSent) {
      return;
    }

    const terminalAdSample = {
      ...buildAdSample({
        playbackSample: this.eventHandler.getCurrentSample(),
        adIndex: this.adIndex,
        currentAdImpressionId: this.currentAdImpressionId!,
        currentAdMetadata: this.currentAdMetadata ?? {},
        quartile: undefined,
        quartileMetadata: undefined,
        errorCode: undefined,
        errorMessage: undefined,
        errorSeverity: undefined,
        timeSinceAdStartedInMs: getMonotonicTimestampInMsSince(this.timestampOfAdStart),
        adPodPosition: this.adPodPosition,
        completedSlates: this.completedSlates,
        completedPaidAds: this.completedPaidAds,
        exitedAdBreak: true,
        closed: 1,
      }),
    };

    this.eventHandler.sendAdSample(terminalAdSample, options);
    this.abandonmentSent = true;
  }

  isInActiveState(): boolean {
    return this.state === SsaiState.ACTIVE;
  }

  reset() {
    this.resetAdBreakData();
    this.adIndex = INITIAL_INDEX_VALUE;
  }

  private resetAdBreakData() {
    this.pendingFirstAdImpressionId = undefined;
    this.adPodPosition = INITIAL_INDEX_VALUE;
    this.state = SsaiState.IDLE;
    this.isFirstSampleOfAd = false;
    this.completedSlates = undefined;
    this.completedPaidAds = undefined;
    delete this.currentAdMetadata;
    this.abandonmentSent = false;
    this.resetAdData();
  }

  private resetAdData() {
    delete this.currentAdMetadata?.customData;
    delete this.currentAdMetadata?.adId;
    delete this.currentAdMetadata?.adSystem;
    this.errorSent = false;
    this.currentAdImpressionId = undefined;
    this.receivedQuartiles = [];
    this.timestampOfAdStart = undefined;
  }
}

function isActivated(eventHandler: SsaiEventHandler | undefined): eventHandler is SsaiEventHandler {
  return isNotNullish(eventHandler);
}
